import {Node, Edge} from "@xyflow/react";
import {FlowNodeData, FlowNodeTypes} from "@/types/node";
import {createNode} from "@/pages/flow/NodeTypes/FlowNode";
import {rootNodeBlueprint} from "@/pages/flow/NodeTypes/Blueprints";
import {globalNodeBlueprint} from "@/pages/flow/NodeTypes/Blueprints/global";
import {startNodeBlueprint} from "@/pages/flow/NodeTypes/Blueprints/start";
import {entryNodeBlueprint} from "@/pages/flow/NodeTypes/Blueprints/entry";

/**
 * Creates default nodes for a new subflow
 */
export const createDefaultNodes = (): Node<FlowNodeData>[] => {
  return [
    createNode(rootNodeBlueprint),
    createNode(entryNodeBlueprint),
    {
      id: "2",
      data: {
        type: FlowNodeTypes.PLACEHOLDER,
      },
      position: {x: 0, y: 250},
      type: "placeholder",
    },
  ];
};

/**
 * Creates default nodes for the main flow (with GLOBAL and START nodes)
 */
export const createMainFlowDefaultNodes = (): Node<FlowNodeData>[] => {
  return [
    createNode(rootNodeBlueprint),
    createNode(globalNodeBlueprint),
    createNode(startNodeBlueprint),
    {
      id: "2",
      data: {
        type: FlowNodeTypes.PLACEHOLDER,
      },
      position: {x: 0, y: 250},
      type: "placeholder",
    },
  ];
};

/**
 * Creates default edges for a new subflow
 */
export const createDefaultEdges = (nodes: Node<FlowNodeData>[]): Edge[] => {
  return [
    {
      id: `root->${nodes?.[1]?.id}`,
      source: nodes?.[0]?.id,
      target: nodes?.[1]?.id,
      type: "workflow",
    },
    {
      id: `${nodes?.[1]?.id}->${nodes?.[2]?.id}`,
      source: nodes?.[1]?.id,
      target: nodes?.[2]?.id,
      type: "workflow",
    },
  ];
};

/**
 * Creates default edges for the main flow (with GLOBAL and START nodes)
 */
export const createMainFlowDefaultEdges = (
  nodes: Node<FlowNodeData>[],
): Edge[] => {
  return [
    {
      id: `root->${nodes?.[1]?.id}`,
      source: nodes?.[0]?.id,
      target: nodes?.[1]?.id,
      type: "workflow",
    },
    {
      id: `${nodes?.[1]?.id}->${nodes?.[2]?.id}`,
      source: nodes?.[1]?.id,
      target: nodes?.[2]?.id,
      type: "workflow",
    },
    {
      id: `${nodes?.[2]?.id}->${nodes?.[3]?.id}`,
      source: nodes?.[2]?.id,
      target: nodes?.[3]?.id,
      type: "workflow",
    },
  ];
};

/**
 * Creates a complete default FlowData for a new subflow
 */
export const createDefaultFlowData = () => {
  const nodes = createDefaultNodes();
  const edges = createDefaultEdges(nodes);

  return {
    name: "",
    live: {
      version: 1,
      nodes,
      edges,
    },
    test: {
      version: 1,
      nodes: [...nodes], // Create a copy for test
      edges: [...edges], // Create a copy for test
    },
  };
};

/**
 * Creates a complete default FlowData for the main flow
 */
export const createMainFlowDefaultFlowData = () => {
  const nodes = createMainFlowDefaultNodes();
  const edges = createMainFlowDefaultEdges(nodes);

  return {
    name: "",
    live: {
      version: 1,
      nodes,
      edges,
    },
    test: {
      version: 1,
      nodes: [...nodes], // Create a copy for test
      edges: [...edges], // Create a copy for test
    },
  };
};
